import { useState } from "react";
import { Link } from "react-router-dom";
import { Input } from "@/components/ui/input";
import { ArrowRight, Hammer, BookOpen, Leaf, BedDouble, Factory, UtensilsCrossed } from "lucide-react";
import { useLanguage } from "@/context/LanguageContext";

const IMAGES = {
  hero: "https://customer-assets.emergentagent.com/job_abule-imole/artifacts/60v2ixvg_WhatsApp%20Image%202026-04-15%20at%2021.15.25.jpeg",
};

const activityImages = [
  "https://customer-assets.emergentagent.com/job_abule-imole/artifacts/g89esfzb_WhatsApp%20Image%202026-04-15%20at%2023.34.47.jpeg",
  "https://customer-assets.emergentagent.com/job_abule-imole/artifacts/nmesl75x_WhatsApp%20Image%202026-04-17%20at%2012.53.24.jpeg",
  "https://customer-assets.emergentagent.com/job_abule-imole/artifacts/1rmn6cs9_WhatsApp%20Image%202026-04-15%20at%2023.38.55.jpeg",
  "https://customer-assets.emergentagent.com/job_abule-imole/artifacts/q3fhgdhv_WhatsApp%20Image%202026-04-15%20at%2023.42.18.jpeg",
  "https://customer-assets.emergentagent.com/job_abule-imole/artifacts/bwkhsxq1_WhatsApp%20Image%202026-04-16%20at%2013.45.31.jpeg",
  "https://images.unsplash.com/photo-1665400808116-f0e6339b7e9a?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODh8MHwxfHNlYXJjaHwyfHxmb29kJTIwY2F0ZXJpbmclMjBzZXJ2aWNlJTIwQWZyaWNhbiUyMGN1aXNpbmV8ZW58MHx8fHwxNzc2MjkwNDIwfDA&ixlib=rb-4.1.0&q=85",
];

const activityIcons = [Hammer, BookOpen, Leaf, BedDouble, Factory, UtensilsCrossed];
const activityKeys = ["building", "education", "farming", "lodging", "manufacturing", "catering"];

function HeroSection() {
  const { t } = useLanguage();
  return (
    <section data-testid="home-hero" className="relative min-h-screen flex items-center overflow-hidden">
      <div className="absolute inset-0 bg-cover bg-center" style={{ backgroundImage: `url(${IMAGES.hero})` }} />
      <div className="absolute inset-0 bg-[#1C2333]/50" />
      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-28 sm:pt-44 pb-32 flex flex-col items-center justify-center text-center min-h-screen">
        <p className="text-sm tracking-[0.2em] uppercase font-semibold text-white mb-4 animate-fade-in-up opacity-0">{t.home.welcome}</p>
        <h1 className="font-['Cormorant_Garamond'] text-4xl sm:text-5xl lg:text-6xl tracking-tight text-white leading-tight animate-fade-in-up opacity-0 animate-delay-100">
          <span className="font-bold">{t.home.title1}</span><br />
          <span className="font-light">{t.home.title2}</span>
        </h1>
        <p className="mt-6 text-white/70 text-base sm:text-xl lg:text-2xl max-w-2xl leading-relaxed animate-fade-in-up opacity-0 animate-delay-200">{t.home.body}</p>
        <div className="mt-8 flex flex-wrap gap-4 justify-center animate-fade-in-up opacity-0 animate-delay-300">
          <Link to="/about" data-testid="hero-cta-about" className="bg-[#C4883A] text-white hover:bg-[#A8722E] px-6 sm:px-10 py-3 sm:py-5 text-base sm:text-xl font-medium transition-all duration-300 inline-flex items-center gap-2">
            {t.home.cta1} <ArrowRight size={20} />
          </Link>
          <Link to="/book" data-testid="hero-cta-book" className="border border-white/40 text-white hover:bg-white/10 px-6 sm:px-10 py-3 sm:py-5 text-base sm:text-xl font-medium transition-all duration-300">
            {t.home.cta2}
          </Link>
        </div>
      </div>
    </section>
  );
}

function ActivityCard({ actKey, icon: Icon, image, isReversed, t }) {
  return (
    <div data-testid={`activity-card-${actKey}`} className={`group flex flex-col ${isReversed ? "md:flex-row-reverse" : "md:flex-row"} items-stretch gap-0 overflow-hidden bg-white border border-[#C8C5BC]/30 shadow-sm hover:shadow-lg transition-all duration-500`}>
      <div className="md:w-1/2 h-64 md:h-80 overflow-hidden relative">
        <img src={image} alt={t.home[`${actKey}_title`]} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
        <div className="absolute inset-0 bg-gradient-to-t md:bg-gradient-to-r from-[#1C2333]/30 to-transparent" />
      </div>
      <div className="md:w-1/2 p-10 sm:p-12 lg:p-16 flex flex-col justify-center relative">
        <div className="w-12 h-12 flex items-center justify-center bg-[#1B3D2F] text-white shadow-md mb-6"><Icon size={20} /></div>
        <h3 className="font-['Cormorant_Garamond'] text-2xl sm:text-3xl font-semibold text-[#1C2333] mb-4">{t.home[`${actKey}_title`]}</h3>
        <p className="text-base sm:text-lg leading-relaxed text-[#1C2333]/70">{t.home[`${actKey}_desc`]}</p>
        <div className="mt-6 w-8 h-[2px] bg-[#C4883A] group-hover:w-16 transition-all duration-500" />
      </div>
    </div>
  );
}

function ActivitiesSection() {
  const { t } = useLanguage();
  return (
    <section data-testid="activities-section" className="py-20 sm:py-32 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <p className="text-base sm:text-lg tracking-[0.2em] uppercase font-semibold text-[#C4883A] mb-4">{t.home.whatWeDo}</p>
          <h2 className="font-['Cormorant_Garamond'] text-4xl sm:text-5xl font-medium tracking-tight text-[#1B3D2F] mb-6">{t.home.ourActivities}</h2>
          <p className="text-lg sm:text-xl lg:text-2xl leading-relaxed text-[#1C2333]/80 max-w-3xl mx-auto">{t.home.activitiesIntro}</p>
        </div>
        <div className="flex flex-col gap-16 lg:gap-24">
          {activityKeys.map((key, idx) => (
            <ActivityCard key={key} actKey={key} icon={activityIcons[idx]} image={activityImages[idx]} isReversed={idx % 2 !== 0} t={t} />
          ))}
        </div>
      </div>
    </section>
  );
}

function NewsletterSection() {
  const { t } = useLanguage();
  const [email, setEmail] = useState("");
  const [subscribed, setSubscribed] = useState(false);
  const handleNewsletter = (e) => { e.preventDefault(); if (email) { setSubscribed(true); setEmail(""); } };

  return (
    <section data-testid="newsletter-section" className="bg-[#D0D5C8] py-20 sm:py-32">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="max-w-2xl mx-auto text-center">
          <p className="text-base sm:text-lg tracking-[0.2em] uppercase font-semibold text-[#C4883A] mb-4">{t.home.stayConnected}</p>
          <h2 className="font-['Cormorant_Garamond'] text-4xl sm:text-5xl font-medium tracking-tight text-[#1C2333] mb-6">{t.home.subscribe}</h2>
          <p className="text-[#1C2333]/70 text-lg sm:text-xl lg:text-2xl leading-relaxed mb-10">{t.home.newsletterBody}</p>
          {subscribed ? (
            <div data-testid="newsletter-success" className="bg-[#A3B18A]/30 border border-[#A3B18A]/50 p-6">
              <p className="text-[#1C2333] font-medium">{t.home.thankYou}</p>
              <p className="text-[#1C2333]/60 text-sm mt-1">{t.home.hearSoon}</p>
            </div>
          ) : (
            <form onSubmit={handleNewsletter} className="flex flex-col sm:flex-row gap-4">
              <Input type="email" placeholder={t.home.emailPlaceholder} value={email} onChange={(e) => setEmail(e.target.value)} required data-testid="newsletter-email-input" className="flex-1 bg-white/70 border-[#A3B18A]/40 text-[#1C2333] placeholder:text-[#1C2333]/40 h-12 rounded-none focus:ring-[#C4883A] focus:border-[#C4883A]" />
              <button type="submit" data-testid="newsletter-submit-btn" className="bg-[#C4883A] text-white hover:bg-[#A8722E] px-8 py-3 font-medium transition-all duration-300 inline-flex items-center justify-center gap-2 whitespace-nowrap">
                {t.home.subscribeBtn} <ArrowRight size={16} />
              </button>
            </form>
          )}
        </div>
      </div>
    </section>
  );
}

export default function Home() {
  return (
    <div data-testid="home-page">
      <HeroSection />
      <ActivitiesSection />
      <NewsletterSection />
    </div>
  );
}
